import React, { useEffect, useRef, useState } from 'react';
import CascaDeModal from '../shared/CascaDeModal';
import { procurementService } from '../../services/api';
import { campo } from '../../design';
import { fmtDate } from '../../utils/formatters';
import { avisoDaAnterior, avisoDoLink, avisoDoLinkAmbiguo, avisoDoLinkMovido, caminhoDaNovaVersao, datasDaNovaVersao, mensagemDasDatasNaoGravadas, mensagemDoErroDaVersao, podeSubirNovaVersao } from '../../utils/novaVersaoDoDocumento';

/**
 * A NOVA VERSÃO de um documento de Licitações (27/10/2026, A1/D1 do Heitor). Abre pelo botão da linha ("Nova versão" ou,
 * na certidão pelo site, depois de abrir o site) ou soltando o arquivo em cima da linha.
 *
 * D1: as datas saem do PDF e aparecem aqui, marcadas "lidas do arquivo"; se a leitura não deu (PDF escaneado), os campos
 * ficam vazios e são pedidos — antes a tela ficava calada com as datas da versão anterior. A confirmação diz o que
 * acontece com a versão atual (ver `caminhoDaNovaVersao`).
 */
export default function NovaVersaoModal({ doc, ativosDoMesmoTipo = 1, arquivoInicial, onClose, onPronto, avisar }: {
  doc: any;
  /** Quantos documentos ATIVOS do mesmo tipo a tela tem (ver caminhoDaNovaVersao). */
  ativosDoMesmoTipo?: number;
  arquivoInicial?: File | null;
  onClose: () => void;
  onPronto: () => void;
  avisar: (msg: string, tipo?: 'success' | 'error') => void;
}) {
  const caminho = caminhoDaNovaVersao(doc, ativosDoMesmoTipo);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [lendo, setLendo] = useState(false);
  const [origem, setOrigem] = useState<'lidas_do_arquivo' | 'parcial' | 'nao_lidas' | null>(null);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [semValidade, setSemValidade] = useState(!!doc?.noExpiry);
  const [subindo, setSubindo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const escolher = async (f: File | null | undefined) => {
    if (!f || subindo) return;
    setArquivo(f);
    setOrigem(null);
    setIssueDate('');
    setExpiryDate('');
    if (f.type !== 'application/pdf') { setOrigem('nao_lidas'); return; }
    setLendo(true);
    try {
      const r = await procurementService.extractDates(f);
      const d = datasDaNovaVersao(r, semValidade);
      setIssueDate(d.issueDate);
      setExpiryDate(d.expiryDate);
      setOrigem(d.origem);
    } catch {
      setOrigem('nao_lidas');
    }
    setLendo(false);
  };

  // O arquivo solto em cima da linha chega pronto: lê as datas na abertura.
  useEffect(() => { if (arquivoInicial) void escolher(arquivoInicial); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subir = async () => {
    if (!podeSubirNovaVersao({ arquivo, issueDate, expiryDate, semValidade }) || !arquivo) return;
    setSubindo(true);
    try {
      const datas = new FormData();
      datas.append('issueDate', issueDate);
      datas.append('expiryDate', semValidade ? '' : expiryDate);
      datas.append('noExpiry', String(semValidade));
      if (caminho === 'por_tipo') {
        // Certidão: o servidor cria a versão nova e guarda a anterior; as datas confirmadas aqui vão em seguida, na nova.
        const r = await procurementService.renewUpload(arquivo, doc.type);
        if (!r?.success) throw new Error(r?.message || 'o servidor recusou o arquivo');
        if (r.documentId) {
          // O PUT valida o documento inteiro (CreateDocumentDto): sem nome e tipo, recusava com 400. Vão os do documento
          // que esta versão substitui, e a versão nova fica com o nome que a tela mostra.
          datas.append('name', doc.name);
          datas.append('type', doc.type);
          try {
            await procurementService.updateDocument(r.documentId, datas);
          } catch (e: any) {
            // A versão já está no ar: a falha é só das datas, e a lista precisa recarregar para mostrar a versão nova.
            avisar(mensagemDasDatasNaoGravadas(e), 'error');
            onPronto();
            setSubindo(false);
            return;
          }
        }
        const ambiguo = avisoDoLinkAmbiguo(r.linkAmbiguo);
        // Um aviso só: o toast tem uma vaga, e o "no ar" depois apagaria o do link.
        if (ambiguo) avisar(`Nova versão de "${doc.name}" no ar. ${ambiguo}`, 'error');
        else avisar(`Nova versão de "${doc.name}" no ar.`);
      } else {
        // A rota de versão (#540): só este documento é desativado, e o servidor diz se moveu o link.
        datas.append('file', arquivo);
        const r = await procurementService.criarVersaoDoDocumento(doc.id, datas);
        const link = avisoDoLinkMovido(r);
        avisar(`Nova versão de "${doc.name}" no ar.${link ? ` ${link}` : ''}`);
      }
      onPronto();
    } catch (e: any) {
      avisar(mensagemDoErroDaVersao(e), 'error');
    }
    setSubindo(false);
  };

  const pode = podeSubirNovaVersao({ arquivo, issueDate, expiryDate, semValidade }) && !lendo && !subindo;
  return (
    <CascaDeModal
      titulo={`Nova versão · ${doc.name}`}
      subtitulo={doc.fileName ? `Versão atual: ${doc.fileName}` : undefined}
      onClose={onClose}
      largura="md"
      rodape={(
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm">Cancelar</button>
          <button type="button" onClick={subir} disabled={!pode} data-subir-nova-versao className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-white text-sm font-medium disabled:opacity-50">
            {subindo ? 'Subindo…' : 'Subir nova versão'}
          </button>
        </div>
      )}
    >
      <div className="space-y-4 text-sm" data-nova-versao={caminho}>
        <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => void escolher(e.target.files?.[0])} />
        <button
          type="button"
          data-zona-da-nova-versao
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => { e.preventDefault(); setArrastando(false); void escolher(e.dataTransfer?.files?.[0]); }}
          className={`w-full rounded-lg border border-dashed px-4 py-5 text-center transition-colors ${arrastando ? 'border-primary ring-2 ring-primary/40 bg-central-elevado' : 'border-central-borda hover:bg-central-elevado'}`}
        >
          {arquivo ? <><b className="text-central-texto">{arquivo.name}</b><div className="text-xs text-slate-400 mt-1">clique ou solte outro para trocar</div></>
            : <><b className="text-central-texto">Escolher o arquivo</b><div className="text-xs text-slate-400 mt-1">ou solte o PDF aqui</div></>}
        </button>

        {arquivo && (
          <div className="space-y-2">
            <div className="text-xs" data-origem-das-datas={origem || 'lendo'}>
              {lendo ? <span className="text-slate-400">Lendo as datas do arquivo…</span>
                : origem === 'lidas_do_arquivo' ? <span className="text-emerald-300">Datas lidas do arquivo. Confira antes de subir.</span>
                : origem === 'parcial' ? <span className="text-amber-300">Só parte das datas foi lida do arquivo. Complete antes de subir.</span>
                : <span className="text-amber-300">Não consegui ler as datas deste arquivo (PDF escaneado ou imagem). Informe antes de subir.</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-400">Emissão *</span>
                <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={`mt-1 w-full rounded-lg px-3 py-2 ${campo}`} />
              </label>
              <label className="block">
                <span className="text-xs text-slate-400">Validade {semValidade ? '' : '*'}</span>
                <input type="date" value={semValidade ? '' : expiryDate} disabled={semValidade} onChange={(e) => setExpiryDate(e.target.value)} className={`mt-1 w-full rounded-lg px-3 py-2 disabled:opacity-50 ${campo}`} />
              </label>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={semValidade} onChange={(e) => setSemValidade(e.target.checked)} /> Não se aplica (documento sem validade)
            </label>
          </div>
        )}

        <p className="text-xs text-slate-400" data-aviso-da-anterior>
          {avisoDaAnterior(caminho, doc.issueDate ? fmtDate(doc.issueDate) : null)}
        </p>
        {avisoDoLink(caminho, !!doc.publicToken) && (
          <p className="text-xs text-slate-400" data-aviso-do-link>{avisoDoLink(caminho, !!doc.publicToken)}</p>
        )}
      </div>
    </CascaDeModal>
  );
}
